import {
  TableRow,
  TableCell,
  Typography,
  Chip,
  Box,
  Stack,
} from '@mui/material';
import { Link } from 'react-router';
import { format, parseISO } from 'date-fns';
import { Model } from '../../models';
import { Api } from '../../api';
import AppleIcon from '../../components/icons/AppleIcon';
import B2BIcon from '../../components/icons/B2BIcon';
import ManualIcon from '../../components/icons/ManualIcon';
import StripeIcon from '../../components/icons/StripeIcon';

interface AppUsersRowProps {
  appUser: Api.User.V_ListAsAdmin;
  /** Name the program the status is for; off when the list is filtered to one program. */
  showProgramTitle: boolean;
}

const SUB_STATUS_LABEL: Record<Model.Subscription.Status, string> = {
  active: 'Active',
  active_ending: 'Active (ending)',
  inactive: 'Inactive',
  licence_revoked: 'Licence revoked',
  payment_failed: 'Payment failure',
  payment_overdue: 'Payment overdue',
  pending: 'Pending',
};

function SubSourceIcon({ source }: { source: Model.Subscription.Source }) {
  const sx = { color: 'text.secondary', display: 'inline-flex' };
  switch (source) {
    case 'stripe':
      return (
        <Box sx={sx}>
          <StripeIcon />
        </Box>
      );
    case 'in_app':
      return (
        <Box sx={sx}>
          <AppleIcon />
        </Box>
      );
    case 'manual':
      return (
        <Box sx={sx}>
          <ManualIcon />
        </Box>
      );
    case 'b2b':
      return (
        <Box sx={sx}>
          <B2BIcon />
        </Box>
      );
  }
}

export default function AppUsersRow({
  appUser,
  showProgramTitle,
}: AppUsersRowProps) {
  const week =
    appUser.programProgress?.currentWeekNumber != null
      ? `${appUser.programProgress.currentWeekNumber} Week`
      : undefined;
  const registrationDate = appUser.user.registeredAt;
  const assignedCoach = appUser.assignedCoachName ?? '-';
  const lmPhysician = appUser.assignedPhysicianName ?? '-';
  const subDisplay = Model.Subscription.toDisplayProps(
    appUser.subscriptionInfo,
  );
  const planDurationLabel = Model.Subscription.getPlanDurationLabel(
    appUser.subscriptionInfo.subscription?.planDuration,
  );
  const { team, organisation } = Model.User.getTeamAndOrganisation(
    appUser.subscriptionInfo,
  );

  return (
    <TableRow
      component={Link}
      to={`/users/${appUser.user.id}/account-details`}
      sx={{
        cursor: 'pointer',
        textDecoration: 'none',
        '&:hover': {
          backgroundColor: 'action.hover',
        },
      }}
    >
      <TableCell>
        <Typography variant="body2" fontWeight={500}>
          {appUser.user.name ?? '-'}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2">{appUser.user.email}</Typography>
      </TableCell>
      <TableCell>
        <Box display="flex" alignItems="center" gap={1}>
          {showProgramTitle && appUser.programTitle && (
            <Typography
              variant="body2"
              noWrap
              title={appUser.programTitle}
              sx={{ maxWidth: 96, flexShrink: 0 }}
            >
              {appUser.programTitle}
            </Typography>
          )}
          <Chip
            label={Model.ProgramProgress.getStatusLabel(
              appUser.programProgress,
            )}
            color={Model.ProgramProgress.getStatusColor(
              appUser.programProgress,
            )}
          />
          {week && (
            <Typography variant="body2" color="textSecondary">
              {week}
            </Typography>
          )}
        </Box>
      </TableCell>
      <TableCell>
        <Typography variant="body2">
          {format(registrationDate, 'd MMM yyyy')}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2">{assignedCoach}</Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2">{lmPhysician}</Typography>
      </TableCell>
      <TableCell>
        <Stack>
          <Stack direction="row" alignItems="flex-start" spacing={1}>
            {subDisplay.source && <SubSourceIcon source={subDisplay.source} />}
            <Stack sx={{ maxWidth: 80 }}>
              <Typography variant="body2">
                {SUB_STATUS_LABEL[subDisplay.status]}
              </Typography>
            </Stack>
          </Stack>
          {subDisplay.endsAt && (
            <Typography variant="caption" color="textSecondary">
              ends {format(parseISO(subDisplay.endsAt), "d MMM ''yy")}
            </Typography>
          )}
        </Stack>
      </TableCell>
      <TableCell>
        {planDurationLabel ||
        appUser.subscriptionRenewed ||
        appUser.supportUnlockPurchased ? (
          <Stack spacing={0.5} alignItems="flex-start">
            {planDurationLabel && (
              <Chip label={planDurationLabel} size="small" variant="outlined" />
            )}
            {appUser.subscriptionRenewed && (
              <Chip label="Renewed" size="small" color="success" />
            )}
            {appUser.supportUnlockPurchased && (
              <Chip label="Support Unlock" size="small" color="primary" />
            )}
          </Stack>
        ) : (
          <Typography variant="body2">-</Typography>
        )}
      </TableCell>
      <TableCell>
        {organisation || team ? (
          <Box>
            {organisation && (
              <Typography variant="body2">{organisation.name}</Typography>
            )}
            {team && (
              <Typography variant="body2" color="textSecondary">
                {team.name}
              </Typography>
            )}
          </Box>
        ) : (
          <Typography variant="body2">-</Typography>
        )}
      </TableCell>
    </TableRow>
  );
}
